import { RecorderBuffer } from "./audioRecorderBuffer";
import {
  RecorderNodeType,
  ScriptWrapper,
  WebAudioWrapper,
  WorkletWrapper,
} from "./webAudioWrapper";
import UAParser from "ua-parser-js";
import { calcPower, detectVoice, detectVoiceV2 } from "./voiceDetection";

const VOLUME_INTERVAL_MILLI_SEC = 200; // DETECT_FRAME_MSECの倍数にする事

// 録音中のみ確保されるリソース
type RecordingSession = {
  updatingIntervalId: number | null;
};

function sleep(msec: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, msec));
}

export class AudioRecorder {
  private _session: RecordingSession | null = null;
  private _recorderBuffer: RecorderBuffer = new RecorderBuffer();
  private _audioWrapper: WebAudioWrapper | null = null;
  private _workletUrl: string;

  // 全ての操作を投入順に直列実行するFIFOキュー
  private _queue: Promise<unknown> = Promise.resolve();

  // WEBAPP-310: iOSで画面ロック→復帰するとAudioContext/MediaStreamSourceが
  // 無効化され、track/contextは生きて見えるのにworkletへ音声が流れず
  // 録音がゼロサイズになる。可視状態が一度hiddenになったら中断フラグを立て、
  // 次の録音開始時（ユーザー操作内）にaudioWrapperを作り直す。
  private _needsReinitialize = false;
  private _visibilityListener: (() => void) | null = null;

  private get _isInitialized(): boolean {
    return this._audioWrapper?.canStart() ?? false;
  }

  private get _needsPrepareWrapper(): boolean {
    return !this._isInitialized || this._needsReinitialize;
  }

  private get _isRecording(): boolean {
    return this._session !== null;
  }

  private _endSession(): void {
    if (this._session === null) return;
    if (this._session.updatingIntervalId !== null) {
      clearInterval(this._session.updatingIntervalId);
    }
    this._session = null;
  }

  get samplingRate(): number | undefined {
    return this._audioWrapper?.samplingRate;
  }

  get audioWrapperType(): RecorderNodeType | undefined {
    return this._audioWrapper?.getType();
  }

  constructor(workletURL: string) {
    this._workletUrl = workletURL;
    // WEBAPP-310: audioRecorderはモジュールシングルトン。可視状態の監視は
    // init/destroyのライフサイクルから独立させ、生成時に一度だけ登録する。
    // （init時登録・destroy時解除にすると、destroy済み〜次のinit前の隙間で
    //  最小化/画面オフを取りこぼし、フラグが立たず作り直しがskipされる）
    this._registerVisibilityListener();
  }

  // 発話検知ロジックはvoiceDetection.tsに分離済み。
  // 既存の呼び出し形(AudioRecorder.detectVoiceV2等)を維持するため再公開する。
  static readonly detectVoice = detectVoice;
  static readonly detectVoiceV2 = detectVoiceV2;

  private clearBuffer(): void {
    this._recorderBuffer.clear();
  }

  private getPower(
    clearBuffer?: boolean,
    threshVolume?: number,
    overThresholdTime?: number,
    lengthMsec?: number,
    detectLengthMsec?: number,
    useMaxVolume?: boolean,
  ): [number | null, number | null, boolean | null, number | null] {
    const data = this.getRecordedData();
    if (clearBuffer) {
      this.clearBuffer();
    }
    return data
      ? calcPower(
          data,
          threshVolume,
          lengthMsec,
          overThresholdTime,
          detectLengthMsec,
          useMaxVolume,
        )
      : [null, null, null, null];
  }

  private getRecordedData(): Float32Array | null {
    return this._recorderBuffer.getData();
  }

  private getAllRecordedData(): Float32Array | null {
    return this._recorderBuffer.getAllData();
  }

  private _enqueue<T>(task: () => Promise<T>): Promise<T> {
    const result = this._queue.then(task);
    // 失敗したタスクで後続を止めないため、キューを繋ぐ側では失敗を吸収する
    this._queue = result.catch(() => undefined);
    // 成否は呼び出し元にのみ伝播する
    return result;
  }

  private _createWebAudioWrapper(): WebAudioWrapper {
    try {
      const uaParserResult = UAParser(window.navigator.userAgent);
      // iOSの場合バージョン
      if (uaParserResult.os.name === "iOS") {
        const version_list = uaParserResult.os.version?.split(".");
        if (version_list && version_list.length >= 2) {
          const major_version = parseInt(version_list[0]);
          const minor_version = parseInt(version_list[1]);

          // iOS 16.3以下の場合はScriptProcessorを使用する
          if (
            major_version < 16 ||
            (major_version == 16 && minor_version <= 3)
          ) {
            return new ScriptWrapper(this._recorderBuffer);
          }
        }
      }
    } catch (e) {
      console.log(e);
    }
    return new WorkletWrapper(this._recorderBuffer, this._workletUrl);
  }

  /**
   * @throws {DOMException}
   */
  async init(): Promise<void> {
    return this._enqueue(() => this._doInit());
  }

  private async _doInit(): Promise<void> {
    if (this._isInitialized) return;
    if (this._isRecording) {
      await this._doStopRecording({});
    }

    await this._prepareWrapper();
  }

  // WEBAPP-310: 画面がhiddenになったら中断フラグを立てる。
  // 復帰(visible)時ではなくhidden検知でフラグを立て、実際の作り直しは
  // 次の録音開始時（ユーザー操作内）に行う。iOSはユーザー操作外の
  // getUserMedia/AudioContext生成が制限されるため。
  private _registerVisibilityListener(): void {
    if (this._visibilityListener) return;
    this._visibilityListener = (): void => {
      if (document.visibilityState === "hidden") {
        this._needsReinitialize = true;
      }
    };
    document.addEventListener("visibilitychange", this._visibilityListener);
  }

  private async _prepareWrapper(): Promise<void> {
    if (this._audioWrapper) {
      await this._audioWrapper.close();
      this._audioWrapper = null;
    }
    try {
      this._audioWrapper = this._createWebAudioWrapper();
      await this._audioWrapper.prepareRecording();
    } catch (e) {
      await this._audioWrapper?.close();
      this._audioWrapper = null;
      throw e;
    }

    this._needsReinitialize = false;
  }

  /**
   * @throws {Error}
   */
  async startRecording(
    updatingPowerCallback?: (
      averagePower: number,
      maxPower: number,
      detectVolumeFlag: boolean,
    ) => void,
    clearBufferOnUpdate?: boolean,
    threshVolume?: number,
    detectLengthMsec?: number,
    useMaxVolume?: boolean,
  ): Promise<void> {
    return this._enqueue(() =>
      this._doStartRecording(
        updatingPowerCallback,
        clearBufferOnUpdate,
        threshVolume,
        detectLengthMsec,
        useMaxVolume,
      ),
    );
  }

  private async _doStartRecording(
    updatingPowerCallback?: (
      averagePower: number,
      maxPower: number,
      detectVolumeFlag: boolean,
    ) => void,
    clearBufferOnUpdate?: boolean,
    threshVolume?: number,
    detectLengthMsec?: number,
    useMaxVolume?: boolean,
  ): Promise<void> {
    if (this._isRecording) {
      await this._doStopRecording({});
    }

    // 録音できるwrapperが無ければここで用意する
    // WEBAPP-310: 画面ロック復帰などで中断されていた場合もここで作り直す
    // （録音開始ボタン押下＝ユーザー操作内で実行される）
    if (this._needsPrepareWrapper) {
      await this._prepareWrapper();
    }

    if (!this._audioWrapper)
      throw new Error("AudioRecorder is not initialized");

    this.clearBuffer();
    this._audioWrapper.startRecording();
    this._session = { updatingIntervalId: null };

    if (updatingPowerCallback) {
      let currentOverThresholdTime = 0;
      this._session.updatingIntervalId = window.setInterval(() => {
        const [averagePower, maxPower, detectVolumeFlag, detectVolumeTime] =
          this.getPower(
            clearBufferOnUpdate,
            threshVolume,
            currentOverThresholdTime,
            VOLUME_INTERVAL_MILLI_SEC,
            detectLengthMsec,
            useMaxVolume,
          );
        if (
          averagePower !== null &&
          maxPower !== null &&
          detectVolumeFlag !== null &&
          detectVolumeTime !== null
        ) {
          if (detectVolumeFlag) {
            currentOverThresholdTime = detectVolumeTime;
          }
          updatingPowerCallback(averagePower, maxPower, detectVolumeFlag);
        }
      }, VOLUME_INTERVAL_MILLI_SEC);
    }
  }

  async stopRecording({
    stopBufferTime = 650,
  }: {
    stopBufferTime?: number;
  }): Promise<Float32Array | null> {
    return this._enqueue(() => this._doStopRecording({ stopBufferTime }));
  }

  private async _doStopRecording({
    stopBufferTime = 650,
  }: {
    stopBufferTime?: number;
  }): Promise<Float32Array | null> {
    if (!this._isRecording) return null;

    // 末尾が切れないよう待機
    await sleep(stopBufferTime);

    const recordedData = this.getAllRecordedData();
    this._endSession();

    try {
      await this._audioWrapper?.stopRecording();
    } catch (e) {
      // ScriptWrapper.stopRecordingはiOS15対策で停止のたびにstreamを取り直す。
      // 失敗しても次回のstartRecordingで作り直されるため、録音データは捨てない。
      console.warn(e);
    } finally {
      this.clearBuffer();
    }
    return recordedData;
  }

  async destroy(): Promise<void> {
    return this._enqueue(() => this._doDestroy());
  }

  private async _doDestroy(): Promise<void> {
    if (this._isRecording) {
      this._endSession();
      try {
        await this._audioWrapper?.stopRecording();
      } catch (e) {
        // destroyは後片付けなので、停止に失敗してもcloseまで進める。
        // ScriptWrapper.stopRecordingはstream再取得を伴い、ページ離脱時に失敗し得る。
        console.warn(e);
      }
    }
    this.clearBuffer();

    if (this._audioWrapper) {
      await this._audioWrapper.close();
      this._audioWrapper = null;
    }
  }
}
